/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react'
import {
  injectStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCVCElement,
  ReactStripeElements,
} from 'react-stripe-elements'
import { t, Trans } from '@lingui/macro'

import { classNames } from 'utils/dom'
import { i18n } from 'utils/i18n'
import { Row, Col } from 'components/grid'

import { useStripeCCFormStateContext } from './StripePaymentForm'
import CardIcon, { SupportedCardType } from './CardIcon'
import {
  inputLabel,
  cardInput,
  cardInputFocus,
  cardInputError,
  errorText,
  cardIcon,
} from './StripePaymentInput.module.scss'

enum CardBrand {
  mastercard = 'mastercard',
  visa = 'visa',
  amex = 'amex',
}

type ElementType = 'cardNumber' | 'cardExpiry' | 'cardCvc'

interface InputLabelProps {
  text: string
  isInputFocused: boolean
  isInputInvalid: boolean
}

interface StripePaymentInputProps {
  className?: string
}

const elementStyle = {
  base: {
    fontFamily: '"Public Sans", sans-serif',
    fontSize: '16px',
    lineHeight: '24px',
    color: '#292c2f',
  },
  invalid: {
    color: '#f34f40',
  },
}

const InputLabel = ({
  text,
  isInputFocused,
  isInputInvalid,
}: InputLabelProps) => (
  <span
    data-is-focused={isInputFocused}
    data-is-invalid={isInputInvalid}
    className={inputLabel}
  >
    {text}
  </span>
)

const invalidState: {
  [key in ElementType]: boolean
} = {
  cardNumber: false,
  cardExpiry: false,
  cardCvc: false,
}

const StripePaymentInput = injectStripe(
  ({
    className = '',
  }: StripePaymentInputProps & ReactStripeElements.InjectedStripeProps) => {
    const [isInputInvalid, setIsInputInvalid] = useState(invalidState)
    const [cardType, setCardType] = useState<SupportedCardType | ''>('')
    const { setIsValid } = useStripeCCFormStateContext()

    const [isPristine, setIsPristine] = useState({
      cardNumber: true,
      cardExpiry: true,
      cardCvc: true,
    })

    const [isInputFocused, setIsInputFocused] = useState({
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    })

    /**
     * brand: mastercard, visa, amex
     */
    const onChange = ({
      elementType,
      complete,
      error,
      empty,
      brand,
    }: ReactStripeElements.ElementChangeResponse & {
      elementType: ElementType
      complete: boolean
      error: string
      empty: boolean
      brand: CardBrand
    }) => {
      setIsPristine({ ...isPristine, [elementType]: empty })
      setIsInputInvalid({ ...isInputInvalid, [elementType]: false })
      setCardType(brand)
      invalidState[elementType] = !!(error || !complete)
    }

    const onFocus = ({
      elementType,
    }: ReactStripeElements.ElementChangeResponse & {
      elementType: ElementType
    }) => {
      setIsInputFocused({ ...isInputFocused, [elementType]: true })
    }

    const onBlur = ({
      elementType,
    }: ReactStripeElements.ElementChangeResponse & {
      elementType: ElementType
    }) => {
      setIsInputFocused({
        ...isInputFocused,
        [elementType]: !isPristine[elementType],
      })
      setIsInputInvalid(invalidState)
    }

    useEffect(() => {
      const isValid = [
        ...Object.values(isInputInvalid),
        ...Object.values(isPristine),
      ].every((bool) => bool === false)
      setIsValid(isValid)
    })

    return (
      <div className={className}>
        <Row>
          <Col>
            <label>
              <InputLabel
                isInputFocused={isInputFocused.cardNumber}
                isInputInvalid={isInputInvalid.cardNumber}
                text={i18n._(t`Credit card number`)}
              />
              <CardIcon cardType={cardType} className={cardIcon} />
              <CardNumberElement
                data-is-focused={isInputFocused.cardNumber}
                data-is-invalid={isInputInvalid.cardNumber}
                className={classNames([
                  cardInput,
                  isInputFocused.cardNumber && cardInputFocus,
                  isInputInvalid.cardNumber && cardInputError,
                ])}
                style={elementStyle}
                onFocus={onFocus}
                onBlur={onBlur}
                onChange={onChange}
                placeholder=""
                id="cardNumber"
              />
              {isInputInvalid.cardNumber && (
                <span className={errorText}>
                  <Trans>Invalid credit card number</Trans>
                </span>
              )}
            </label>
          </Col>
        </Row>
        <Row>
          <Col>
            <label>
              <InputLabel
                isInputFocused={isInputFocused.cardExpiry}
                isInputInvalid={isInputInvalid.cardExpiry}
                text={i18n._(t`MM / YY`)}
              />
              <CardExpiryElement
                data-is-focused={isInputFocused.cardExpiry}
                data-is-invalid={isInputInvalid.cardExpiry}
                className={classNames([
                  cardInput,
                  isInputFocused.cardExpiry && cardInputFocus,
                  isInputInvalid.cardExpiry && cardInputError,
                ])}
                style={elementStyle}
                onFocus={onFocus}
                onBlur={onBlur}
                onChange={onChange}
                placeholder=""
              />
              {isInputInvalid.cardExpiry && (
                <span className={errorText}>
                  <Trans>Invalid date</Trans>
                </span>
              )}
            </label>
          </Col>
          <Col>
            <label>
              <InputLabel
                isInputFocused={isInputFocused.cardCvc}
                isInputInvalid={isInputInvalid.cardCvc}
                text={i18n._(t`CVC`)}
              />
              <CardCVCElement
                data-is-focused={isInputFocused.cardCvc}
                data-is-invalid={isInputInvalid.cardCvc}
                className={classNames([
                  cardInput,
                  isInputFocused.cardCvc && cardInputFocus,
                  isInputInvalid.cardCvc && cardInputError,
                ])}
                style={elementStyle}
                onFocus={onFocus}
                onBlur={onBlur}
                onChange={onChange}
                placeholder=""
              />
              {isInputInvalid.cardCvc && (
                <span className={errorText}>
                  <Trans>Invalid CVC</Trans>
                </span>
              )}
            </label>
          </Col>
        </Row>
      </div>
    )
  }
)

export default StripePaymentInput
